import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  PAYPAL_CONFIG,
  createCheckoutSession,
  createPayPalOrder,
  handlePayPalApproval,
  handlePayPalError,
  applyDiscount
} from '../../services/paymentService';
import { MEMBERSHIP_TIERS } from '../../constants/membershipTiers';
import { useAuth } from '../../contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const DualPaymentForm = ({ selectedTier, discountCode, onSuccess, onError }) => {
  const { currentUser } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'paypal'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fallback membership tiers in case import fails
  const fallbackTiers = {
    TIER1: { id: 'tier1', name: 'Tier 1', price: 75, priceId: 'price_tier1_annual' },
    TIER2: { id: 'tier2', name: 'Tier 2', price: 125, priceId: 'price_tier2_annual' },
    TIER3: { id: 'tier3', name: 'Tier 3', price: 175, priceId: 'price_tier3_annual' },
    TIER4: { id: 'tier4', name: 'Tier 4', price: 225, priceId: 'price_tier4_annual' },
    TIERUSV: { id: 'tierUSV', name: 'Tier USV', price: 75, priceId: 'price_tierUSV_annual' }
  };

  const tiersToUse = MEMBERSHIP_TIERS && Object.keys(MEMBERSHIP_TIERS).length > 0 ? MEMBERSHIP_TIERS : fallbackTiers;
  const tier = tiersToUse[selectedTier];

  // Debug logging - KEEP THIS FOR NOW
  console.log('DualPaymentForm - selectedTier:', selectedTier);
  console.log('DualPaymentForm - MEMBERSHIP_TIERS:', MEMBERSHIP_TIERS);
  console.log('DualPaymentForm - tiersToUse:', tiersToUse);
  console.log('DualPaymentForm - tier:', tier);

  if (!tier) {
    return <div className="text-red-600">Invalid membership tier selected: {selectedTier}. Available tiers: {Object.keys(tiersToUse).join(', ')}</div>;
  }

  // Calculate final price with discount
  const discountResult = discountCode ? applyDiscount(tier.price, discountCode) : { finalPrice: tier.price, discountAmount: 0 };
  const { finalPrice, discountAmount } = discountResult;

  // Handle Stripe payment
  const handleStripePayment = async () => {
    if (!currentUser) {
      setError('Please log in to continue with payment.');
      return;
    }

    // Prevent multiple simultaneous payment attempts
    if (loading) {
      console.log('Payment already in progress, ignoring duplicate request');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Starting Stripe payment for:', {
        userId: currentUser.uid,
        selectedTier,
        discountCode
      });

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const sessionData = await createCheckoutSession(currentUser.uid, selectedTier, discountCode);
      console.log('Checkout session created:', sessionData);

      if (!sessionData || !sessionData.id) {
        throw new Error('Invalid session data received');
      }

      // Clear loading state before redirect to prevent UI issues
      setLoading(false);

      console.log('About to redirect to Stripe checkout with session:', sessionData.id);
      console.log('Success URL should be:', `${window.location.origin}/membership/success`);

      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionData.id
      });

      if (error) {
        console.error('Stripe redirect error:', error);
        setError(error.message);
        onError?.(error);
        setLoading(false);
      }
    } catch (err) {
      console.error('Stripe payment error:', err);
      setError(`Failed to process payment: ${err.message}`);
      onError?.(err);
      setLoading(false);
    }
  };

  // PayPal payment handlers
  const createPayPalOrderHandler = async () => {
    if (!currentUser) {
      setError('Please log in to continue with payment.');
      return;
    }

    // Prevent multiple simultaneous order creation
    if (loading) {
      console.log('Order creation already in progress, ignoring duplicate request');
      return;
    }

    try {
      console.log('Creating PayPal order for:', {
        userId: currentUser.uid,
        selectedTier,
        discountCode
      });

      const orderId = await createPayPalOrder(currentUser.uid, selectedTier, discountCode);
      console.log('PayPal order ID created:', orderId);
      return orderId;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      setError(`Failed to create PayPal order: ${error.message}`);
      throw error;
    }
  };

  const onPayPalApprove = async (data, actions) => {
    // Prevent multiple simultaneous approvals
    if (loading) {
      console.log('Payment approval already in progress, ignoring duplicate request');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const orderData = await actions.order.capture();
      const result = await handlePayPalApproval(orderData, currentUser.uid, selectedTier);

      if (result.success) {
        onSuccess?.(result.membershipData);
      } else {
        throw new Error('Payment processing failed');
      }
    } catch (error) {
      console.error('PayPal approval error:', error);
      const errorResult = handlePayPalError(error);
      setError(errorResult.error);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  const onPayPalError = (error) => {
    console.error('PayPal error:', error);
    const errorResult = handlePayPalError(error);
    setError(errorResult.error);
    onError?.(error);
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
      {/* Order Summary */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span>{tier.name} Membership</span>
            <span>${tier.price}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between items-center mb-2 text-green-600">
              <span>Discount ({discountCode})</span>
              <span>-${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <hr className="my-2" />
          <div className="flex justify-between items-center font-semibold">
            <span>Total</span>
            <span>${finalPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">Choose Payment Method</h3>
        <div className="space-y-3">
          <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="paymentMethod"
              value="stripe"
              checked={paymentMethod === 'stripe'}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="mr-3"
            />
            <div className="flex items-center">
              <span className="font-medium">Credit/Debit Card</span>
              <div className="ml-2 flex space-x-1">
                <img src="/images/payment/visa.png" alt="Visa" className="h-6" onError={(e) => e.target.style.display = 'none'} />
                <img src="/images/payment/mastercard.png" alt="Mastercard" className="h-6" onError={(e) => e.target.style.display = 'none'} />
                <img src="/images/payment/amex.png" alt="American Express" className="h-6" onError={(e) => e.target.style.display = 'none'} />
              </div>
            </div>
          </label>
          
          <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="paymentMethod"
              value="paypal"
              checked={paymentMethod === 'paypal'}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="mr-3"
            />
            <div className="flex items-center">
              <span className="font-medium">PayPal</span>
              <img src="/images/payment/paypal.png" alt="PayPal" className="ml-2 h-6" onError={(e) => e.target.style.display = 'none'} />
            </div>
          </label>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Payment Buttons */}
      <div className="space-y-4">
        {paymentMethod === 'stripe' && (
          <button
            onClick={handleStripePayment}
            disabled={loading}
            className="w-full bg-apn-blue text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : `Pay $${finalPrice.toFixed(2)} with Card`}
          </button>
        )}

        {paymentMethod === 'paypal' && PAYPAL_CONFIG.clientId && (
          <PayPalScriptProvider options={{
            "client-id": PAYPAL_CONFIG.clientId,
            currency: PAYPAL_CONFIG.currency,
            intent: PAYPAL_CONFIG.intent
          }}>
            <PayPalButtons
              style={{
                layout: 'vertical',
                color: 'blue',
                shape: 'rect',
                label: 'pay'
              }}
              createOrder={createPayPalOrderHandler}
              onApprove={onPayPalApprove}
              onError={onPayPalError}
              disabled={loading}
            />
          </PayPalScriptProvider>
        )}

        {paymentMethod === 'paypal' && !PAYPAL_CONFIG.clientId && (
          <div className="text-center text-gray-500 py-4">
            PayPal is not configured. Please contact support.
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>🔒 Your payment information is secure and encrypted</p>
      </div>
    </div>
  );
};

export default DualPaymentForm;
